import { useQuery } from '@tanstack/react-query';
import { isVantageReasonCodesManagementEnabled } from '@/services/systemConfiguration.utilities';
import { logError } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { treeOutputToEditorState } from '@/vantage/components/extensionConfig/reasonCodeManagement/reasonCode.utilities';
import { reasonCodeRoutingFromAssignments } from '@/vantage/reasonCodesExtension.utilities';
import { ListReasonCodeHierarchiesQueryKey, sqContextApi } from '@seeqdev/api/endpoint/ContextApi';
import type { UseQueryResult } from '@tanstack/react-query';
import type { AssignedReasonCodeHierarchy, ReasonCodeRouting } from '@/vantage/reasonCodesExtension.utilities';
import type { ReasonCodeConditionAssignmentOutputV1 } from '@seeqdev/api/model/ReasonCodeConditionAssignmentOutputV1';

type RoomReasonCodes = {
  hierarchies: AssignedReasonCodeHierarchy[];
  conditionAssignments: ReasonCodeConditionAssignmentOutputV1[];
  assignedLabelIds: string[];
};

/** Prefix of every generated tree query key (GetReasonCodeHierarchyTreeQueryKey), for invalidating all trees. */
export const reasonCodeHierarchyTreesQueryKey = ['sqContextApi', 'getReasonCodeHierarchyTree'] as const;

// Extends the list endpoint's key so invalidating the list also refreshes this aggregate.
const roomReasonCodesQueryKey = (workbookId: string | undefined) =>
  [...ListReasonCodeHierarchiesQueryKey({ workbookId }), 'withLevels'] as const;

/** The room's assigned, non-archived hierarchies with level names, plus the raw condition assignments. */
export const useRoomReasonCodesQuery = (workbookId: string | undefined): UseQueryResult<RoomReasonCodes> =>
  useQuery<RoomReasonCodes>({
    queryKey: roomReasonCodesQueryKey(workbookId),
    enabled: !!workbookId && isVantageReasonCodesManagementEnabled(),
    queryFn: async () => {
      const { data } = await sqContextApi.listReasonCodeHierarchies({ workbookId: workbookId! });
      const conditionAssignments = data.conditionAssignments ?? [];
      const assignedIds = new Set(conditionAssignments.map((a) => a.hierarchyCategoryId));
      const assigned = (data.hierarchies ?? []).filter((h) => !h.archived && h.id && assignedIds.has(h.id));
      const trees = await Promise.allSettled(
        assigned.map(async (h) => {
          const tree = await sqContextApi.getReasonCodeHierarchyTree({ categoryId: h.id! });
          // The tree's levels are name-ordered; the editor state derives them in depth order.
          const levels = treeOutputToEditorState(tree.data)
            .levels.map((level) => level.name)
            .filter(Boolean);
          const labelIds = (tree.data.levels ?? [])
            .flatMap((level) => level.labels ?? [])
            .map((label) => label.id)
            .filter((id): id is string => Boolean(id))
            .map((id) => id.toUpperCase());
          return { hierarchy: { id: h.id!, name: h.name ?? '', levels }, labelIds };
        }),
      );
      // One broken hierarchy must not hide the others' buttons and columns.
      trees.forEach((tree, index) => {
        if (tree.status === 'rejected') {
          logError(formatMessage`Failed to load reason code hierarchy ${assigned[index].name}: ${tree.reason}`);
        }
      });
      const loaded = trees
        .filter(
          (tree): tree is PromiseFulfilledResult<{ hierarchy: AssignedReasonCodeHierarchy; labelIds: string[] }> =>
            tree.status === 'fulfilled',
        )
        .map((tree) => tree.value);
      return {
        hierarchies: loaded.map((entry) => entry.hierarchy),
        conditionAssignments,
        assignedLabelIds: loaded.flatMap((entry) => entry.labelIds),
      };
    },
  });

/** The room's condition routing and assigned label ids; empty while inactive, loading, or errored. */
export const useReasonCodeRouting = (
  workbookId: string | undefined,
): {
  routing: ReasonCodeRouting;
  assignedLabelIds: string[];
  isRoutingLoading: boolean;
  routingError: Error | null;
} => {
  const { data, isLoading, error } = useRoomReasonCodesQuery(workbookId);
  return {
    routing: reasonCodeRoutingFromAssignments(data?.conditionAssignments ?? []),
    assignedLabelIds: data?.assignedLabelIds ?? [],
    isRoutingLoading: isLoading,
    routingError: error,
  };
};
